import React, { useEffect, useState, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { db } from "../firebaseConfig.js";
import {
  collection, addDoc, onSnapshot, query, orderBy, Timestamp, doc, updateDoc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ClearIcon from "@mui/icons-material/Clear";
import { agentState } from "../App.js";
import { useRecoilState } from "recoil";

// ─── Global Fonts ─────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist:wght@300;400;500;600&family=Geist+Mono:wght@400;500&display=swap');
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPPLIERS = [
  "Masterfare", "Brightsun", "Polani", "Air Arabia", "Air Asia", "Al Qemah",
  "British Airways", "EasyJet", "FlyNas", "Hassan Kamran Travel",
  "Heavan Travel & Tours", "National Air Service", "NO1LOUNGES", "Panama",
  "Pegasus","AeroGlobe","Etihad Airways","Citibond","Virgin Atlantic","Akbar Travels", "Ryan Air", "Sasta Ticket", "Visa Club Travel", "Wizz Air", "Profine Travel",
];

const ENTRY_TYPES = ["NEW", "REISSUANCE", "PAYMENT", "REFUND"];
const PAGE_SIZE   = 100;

const EMPTY_FORM = {
  agent: "", folderNo: "", pnr: "", cost: "", supplier: "",
  date: "", travelDate: "", issuedBy: "", type: "", remarks: "",
};

const EMPTY_FILTERS = {
  agent: "", supplier: "", startDate: "", endDate: "",
  folderSearch: "", pnrSearch: "",
};

const TYPE_META = {
  NEW:        { color: "#059669", bg: "#ecfdf5", border: "#6ee7b7", label: "New" },
  REISSUANCE: { color: "#2563eb", bg: "#eff6ff", border: "#93c5fd", label: "Reissuance" },
  PAYMENT:    { color: "#d97706", bg: "#fffbeb", border: "#fcd34d", label: "Payment" },
  REFUND:     { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5", label: "Refund" },
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0); max-height: 1000px; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalize = (v) => String(v || "").trim().toLowerCase();
const initials  = (n) => (n || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
const fmtDate   = (s) => s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtMoney  = (n) => `£${Math.abs(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AVATAR_PALETTES = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#15803d"], ["#fef3c7", "#b45309"],
  ["#fce7f3", "#be185d"], ["#ede9fe", "#6d28d9"], ["#e0f2fe", "#0369a1"],
  ["#ffedd5", "#c2410c"], ["#f0fdf4", "#166534"],
];
const avatarPalette = (name) => AVATAR_PALETTES[name.charCodeAt(0) % AVATAR_PALETTES.length];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FormField = ({ label, children, style }) => (
  <FieldWrap style={style}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </FieldWrap>
);

const StyledSelect = ({ name, value, onChange, options, placeholder }) => (
  <InputEl as="select" name={name} value={value} onChange={onChange}>
    <option value="">{placeholder || `— Select —`}</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </InputEl>
);

const StyledInput = ({ name, value, onChange, type = "text", placeholder }) => (
  <InputEl name={name} value={value} onChange={onChange} type={type} placeholder={placeholder} />
);

const StatCard = ({ label, value, sub, accent }) => (
  <StatCardWrap $accent={accent}>
    <StatLabel>{label}</StatLabel>
    <StatValue $accent={accent}>{value}</StatValue>
    {sub && <StatSub>{sub}</StatSub>}
  </StatCardWrap>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const FlightSupplierLedger = () => {
  const [salesPerson] = useRecoilState(agentState);
  const agents = salesPerson.map(item => item.id);

  const [entries,    setEntries]    = useState([]);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [form2,      setForm2]      = useState(EMPTY_FORM);
  const [filters,    setFilters]    = useState(EMPTY_FILTERS);
  const [editId,     setEditId]     = useState(null);
  const [editOpen,   setEditOpen]   = useState(false);
  const [addOpen,    setAddOpen]    = useState(false);
  const [otpEntered, setOtpEntered] = useState("");
  const [search,     setSearch]     = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "timestamp", dir: "desc" });
  const [activeType, setActiveType] = useState("ALL");

  // ── Pagination state ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── Body scroll lock ────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = editOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [editOpen]);

  // ── Escape key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && editOpen) { setEditOpen(false); setOtpEntered(""); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editOpen]);

  // ── Firestore ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, "flightSupplierLedger"), orderBy("timestamp", "desc"));
    return onSnapshot(q, snap => {
      setEntries(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        timestamp: d.data().timestamp?.toDate?.() ?? new Date(),
      })));
    });
  }, []);

  // ── Reset to page 1 when filters/search/type/sort change ───────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, search, activeType, sortConfig]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange  = e => setForm(p  => ({ ...p,  [e.target.name]: e.target.value }));
  const handleChange2 = e => setForm2(p => ({ ...p,  [e.target.name]: e.target.value }));
  const handleFilter  = e => setFilters(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleAddEntry = async () => {
    if (!form.agent || !form.supplier) return alert("Agent & Supplier required");
    if (!form.cost) return alert("Enter cost");
    try {
      await addDoc(collection(db, "flightSupplierLedger"), {
        ...form, cost: parseFloat(form.cost || 0),
        folderNo: form.folderNo || "-", pnr: form.pnr || "-",
        timestamp: Timestamp.now(),
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
    } catch { alert("Failed to add entry"); }
  };

  const handleEditClick = id => {
    const sel = entries.find(e => e.id === id);
    if (!sel) return;
    setForm2({ ...sel });
    setEditId(id);
    setEditOpen(true);
  };

  const handleUpdateEntry = async () => {
    if (Number(otpEntered) !== 5740) return alert("Edit code incorrect");
    try {
      await updateDoc(doc(db, "flightSupplierLedger", editId), {
        ...form2, cost: parseFloat(form2.cost || 0),
      });
      setEditOpen(false);
      setOtpEntered("");
    } catch { alert("Update failed"); }
  };

  const handleExport = () => {
    if (!filteredEntries.length) return alert("No data to export");
    const ws = XLSX.utils.json_to_sheet(filteredEntries.map(e => ({
      Agent: e.agent, "Folder #": e.folderNo, PNR: e.pnr, "Cost (£)": e.cost,
      Supplier: e.supplier, Type: e.type, "Issuance Date": e.date,
      "Travel Date": e.travelDate, "Issued By": e.issuedBy, Remarks: e.remarks,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Flight Ledger");
    XLSX.writeFile(wb, `FlightLedger_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleSort = key =>
    setSortConfig(p => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));

  // ── Filtering ────────────────────────────────────────────────────────────────
  const filteredEntries = useMemo(() => {
    let data = [...entries];

    if (activeType !== "ALL")
      data = data.filter(e => e.type === activeType);

    if (filters.agent)
      data = data.filter(e => normalize(e.agent) === normalize(filters.agent));

    if (filters.supplier)
      data = data.filter(e => normalize(e.supplier) === normalize(filters.supplier));

    if (filters.folderSearch)
      data = data.filter(e =>
        normalize(e.folderNo).includes(normalize(filters.folderSearch))
      );

    if (filters.pnrSearch)
      data = data.filter(e =>
        normalize(e.pnr).includes(normalize(filters.pnrSearch))
      );

    if (filters.startDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);
      data = data.filter(e => {
        const d = e.date ? new Date(e.date) : null;
        return d && d >= start;
      });
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      data = data.filter(e => {
        const d = e.date ? new Date(e.date) : null;
        return d && d <= end;
      });
    }

    if (search) {
      const q = normalize(search);
      data = data.filter(e =>
        normalize(e.agent).includes(q) ||
        normalize(e.supplier).includes(q) ||
        normalize(e.folderNo).includes(q) ||
        normalize(e.pnr).includes(q) ||
        normalize(e.issuedBy).includes(q) ||
        normalize(e.remarks).includes(q)
      );
    }

    data.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      if (db !== da) return db - da;

      let av = a[sortConfig.key], bv = b[sortConfig.key];
      if (sortConfig.key === "cost")      return sortConfig.dir === "asc" ? av - bv : bv - av;
      if (sortConfig.key === "timestamp") return sortConfig.dir === "asc" ? av - bv : bv - av;
      av = String(av || "").toLowerCase();
      bv = String(bv || "").toLowerCase();
      return sortConfig.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });

    return data;
  }, [entries, filters, search, activeType, sortConfig]);

  // ── Pagination derived values ───────────────────────────────────────────────
  const totalPages   = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
  const safePage     = Math.min(currentPage, totalPages);
  const pageStart    = (safePage - 1) * PAGE_SIZE;
  const pageEnd      = pageStart + PAGE_SIZE;
  const pagedEntries = filteredEntries.slice(pageStart, pageEnd);

  const goToPage = (p) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    setCurrentPage(clamped);
    document.getElementById("flight-ledger-table-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (safePage <= 4) {
      pages.push(1, 2, 3, 4, 5, "…", totalPages);
    } else if (safePage >= totalPages - 3) {
      pages.push(1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "…", safePage - 1, safePage, safePage + 1, "…", totalPages);
    }
    return pages;
  }, [safePage, totalPages]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    filteredEntries.reduce((s, e) => s + (e.cost || 0), 0),
    newCount: filteredEntries.filter(e => e.type === "NEW").length,
    refunds:  filteredEntries.filter(e => e.type === "REFUND").reduce((s, e) => s + (e.cost || 0), 0),
    count:    filteredEntries.length,
  }), [filteredEntries]);

  const hasFilters   = filters.agent || filters.supplier || filters.folderSearch || filters.pnrSearch || filters.startDate || filters.endDate || search || activeType !== "ALL";
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch(""); setActiveType("ALL"); };

  const SortIcon = ({ col }) => (
    <SortIndicator $active={sortConfig.key === col}>
      {sortConfig.key === col ? (sortConfig.dir === "asc" ? "↑" : "↓") : "↕"}
    </SortIndicator>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyle />
      <Page>

        {/* ── Header ── */}
        <Header>
          <HeaderContent>
            <HeaderEyebrow>Financial Operations</HeaderEyebrow>
            <HeaderTitle>Flight Supplier<TitleAccent> Ledger</TitleAccent></HeaderTitle>
            <HeaderSub>Track, manage, and analyse all flight supplier transactions</HeaderSub>
          </HeaderContent>
          <HeaderActions>
            <ExportBtn onClick={handleExport}>
              <BtnIcon>↓</BtnIcon> Export Excel
            </ExportBtn>
            <AddBtn onClick={() => setAddOpen(p => !p)}>
              <BtnIcon>{addOpen ? "×" : "+"}</BtnIcon>
              {addOpen ? "Cancel" : "New Entry"}
            </AddBtn>
          </HeaderActions>
        </Header>

        {/* ── Stat Cards ── */}
        <StatsGrid>
          <StatCard label="Total Volume"   value={fmtMoney(stats.total)}   sub={`${stats.count} entries`}        accent />
          <StatCard label="New Issuances"  value={stats.newCount}           sub="tickets issued" />
          <StatCard label="Refunds Issued" value={fmtMoney(stats.refunds)} sub="total refunded" />
          <StatCard label="Showing"        value={filteredEntries.length}   sub={`of ${entries.length} entries`} />
        </StatsGrid>

        {/* ── Add Entry Panel ── */}
        {addOpen && (
          <Panel>
            <PanelHeader>
              <PanelTitle><PanelDot $color="#2563eb" />New Ledger Entry</PanelTitle>
            </PanelHeader>
            <PanelBody>
              <FormGrid>
                <FormField label="Agent">
                  <StyledSelect name="agent" value={form.agent} onChange={handleChange} options={agents} />
                </FormField>
                <FormField label="Supplier">
                  <StyledSelect name="supplier" value={form.supplier} onChange={handleChange} options={SUPPLIERS} />
                </FormField>
                <FormField label="Entry Type">
                  <StyledSelect name="type" value={form.type} onChange={handleChange} options={ENTRY_TYPES} />
                </FormField>
                <FormField label="Folder No">
                  <StyledInput name="folderNo" value={form.folderNo} onChange={handleChange} placeholder="e.g. 10452" />
                </FormField>
                <FormField label="PNR">
                  <StyledInput name="pnr" value={form.pnr} onChange={handleChange} placeholder="e.g. ABC123" />
                </FormField>
                <FormField label="Cost (£)">
                  <StyledInput name="cost" value={form.cost} onChange={handleChange} type="number" placeholder="0.00" />
                </FormField>
                <FormField label="Issued By">
                  <StyledInput name="issuedBy" value={form.issuedBy} onChange={handleChange} placeholder="Staff name" />
                </FormField>
                <FormField label="Issuance Date">
                  <StyledInput name="date" value={form.date} onChange={handleChange} type="date" />
                </FormField>
                <FormField label="Departure Date">
                  <StyledInput name="travelDate" value={form.travelDate} onChange={handleChange} type="date" />
                </FormField>
                <FormField label="Remarks" style={{ flex: 2 }}>
                  <StyledInput name="remarks" value={form.remarks} onChange={handleChange} placeholder="Optional notes…" />
                </FormField>
              </FormGrid>
              <PanelFooter>
                <SubmitBtn onClick={handleAddEntry}>Add Entry →</SubmitBtn>
              </PanelFooter>
            </PanelBody>
          </Panel>
        )}

        {/* ── Filter + Search Bar ── */}
        <FilterBar>
          <SearchWrap>
            <SearchIconEl>⌕</SearchIconEl>
            <SearchInput
              placeholder="Search entries — agent, supplier, folder, PNR…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchWrap>

          <FilterControls>
            <FilterGroup>
              <FilterLabel>Agent</FilterLabel>
              <FilterSelect name="agent" value={filters.agent} onChange={handleFilter}>
                <option value="">All agents</option>
                {agents.map(a => <option key={a} value={a}>{a}</option>)}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Supplier</FilterLabel>
              <FilterSelect name="supplier" value={filters.supplier} onChange={handleFilter}>
                <option value="">All suppliers</option>
                {SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Folder No</FilterLabel>
              <FilterInput name="folderSearch" value={filters.folderSearch} onChange={handleFilter} placeholder="Search…" />
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>PNR</FilterLabel>
              <FilterInput name="pnrSearch" value={filters.pnrSearch} onChange={handleFilter} placeholder="Search…" />
            </FilterGroup>

            <DateRangeGroup>
              <FilterLabel>Issuance Date</FilterLabel>
              <DateRangeRow>
                <DateRangeInput
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilter}
                  title="From date"
                />
                <DateRangeSep>→</DateRangeSep>
                <DateRangeInput
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilter}
                  title="To date"
                />
              </DateRangeRow>
            </DateRangeGroup>

            {hasFilters && (
              <ClearAllBtn onClick={clearFilters}>✕ Clear all</ClearAllBtn>
            )}
          </FilterControls>

          {(filters.startDate || filters.endDate) && (
            <ActiveDateRange>
              <ActiveDateIcon>📅</ActiveDateIcon>
              <span>
                Issuance date:&nbsp;
                <strong>{filters.startDate ? fmtDate(filters.startDate) : "any"}</strong>
                &nbsp;→&nbsp;
                <strong>{filters.endDate ? fmtDate(filters.endDate) : "any"}</strong>
              </span>
              <ClearDateBtn onClick={() => setFilters(p => ({ ...p, startDate: "", endDate: "" }))}>
                ✕
              </ClearDateBtn>
            </ActiveDateRange>
          )}
        </FilterBar>

        {/* ── Type Tabs ── */}
        <TypeTabs>
          {["ALL", ...ENTRY_TYPES].map(t => {
            const count = t === "ALL" ? entries.length : entries.filter(e => e.type === t).length;
            const meta  = TYPE_META[t];
            return (
              <TypeTab key={t} $active={activeType === t} $meta={meta} onClick={() => setActiveType(t)}>
                {t === "ALL" ? "All Types" : (meta?.label || t)}
                <TypeCount $active={activeType === t}>{count}</TypeCount>
              </TypeTab>
            );
          })}
        </TypeTabs>

        {/* ── Table ── */}
        <TableCard id="flight-ledger-table-card">
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: 44 }}>#</Th>
                  <Th sortable onClick={() => handleSort("agent")}>Agent <SortIcon col="agent" /></Th>
                  <Th sortable onClick={() => handleSort("folderNo")}>Folder <SortIcon col="folderNo" /></Th>
                  <Th>PNR</Th>
                  <Th sortable onClick={() => handleSort("cost")}>Cost <SortIcon col="cost" /></Th>
                  <Th sortable onClick={() => handleSort("supplier")}>Supplier <SortIcon col="supplier" /></Th>
                  <Th sortable onClick={() => handleSort("date")}>Issuance <SortIcon col="date" /></Th>
                  <Th sortable onClick={() => handleSort("travelDate")}>Departure <SortIcon col="travelDate" /></Th>
                  <Th>Issued By</Th>
                  <Th>Type</Th>
                  <Th>Remarks</Th>
                  <Th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {pagedEntries.length === 0 && (
                  <tr>
                    <td colSpan={12}>
                      <EmptyState>
                        <EmptyIcon>✈</EmptyIcon>
                        <EmptyTitle>Loading…</EmptyTitle>
                        <EmptyDesc>{hasFilters ? "Try adjusting or clearing your filters" : ""}</EmptyDesc>
                      </EmptyState>
                    </td>
                  </tr>
                )}

                {pagedEntries.map((e, idx) => {
                  const globalIdx  = pageStart + idx;
                  const type       = TYPE_META[e.type] || { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" };
                  const [aBg, aFg] = avatarPalette(e.agent || "?");
                  const isNeg      = (e.cost || 0) < 0;
                  return (
                    <TableRow key={e.id} $idx={idx}>
                      <Td $muted>{globalIdx + 1}</Td>
                      <Td>
                        <AgentCell>
                          <Avatar style={{ background: aBg, color: aFg }}>{initials(e.agent)}</Avatar>
                          <AgentName>{e.agent || "—"}</AgentName>
                        </AgentCell>
                      </Td>
                      <Td
                      onClick={() => {
      const url = `/FolderLedger?FolderNumber=${String(e.folderNo)}`;
      window.open(url, "_blank");
    }}
    style={{ cursor: "pointer" }}><FolderTag>#{e.folderNo}</FolderTag></Td>
                      <Td $mono>{e.pnr || "—"}</Td>
                      <Td>
                        <CostCell $negative={isNeg}>
                          {isNeg ? "−" : "+"}{fmtMoney(e.cost)}
                        </CostCell>
                      </Td>
                      <Td>
                        <SupplierCell>
                          <SupplierDot style={{ background: type.color }} />
                          {e.supplier || "—"}
                        </SupplierCell>
                      </Td>
                      <Td $mono $muted>{fmtDate(e.date)}</Td>
                      <Td $mono $muted>{fmtDate(e.travelDate)}</Td>
                      <Td $muted>{e.issuedBy || "—"}</Td>
                      <Td>
                        <TypeBadge $color={type.color} $bg={type.bg} $border={type.border}>
                          {type.label || e.type}
                        </TypeBadge>
                      </Td>
                      <Td><RemarksText title={e.remarks}>{e.remarks || "—"}</RemarksText></Td>
                      <Td>
                        <EditBtn onClick={() => handleEditClick(e.id)} title="Edit entry">
                          <ModeEditIcon style={{ fontSize: 14 }} />
                        </EditBtn>
                      </Td>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>

          <TableFooter>
            <FooterLeft>
              Showing{" "}
              <FooterStrong>{filteredEntries.length === 0 ? 0 : pageStart + 1}–{Math.min(pageEnd, filteredEntries.length)}</FooterStrong>
              {" "}of{" "}
              <FooterStrong>{filteredEntries.length}</FooterStrong> entries
            </FooterLeft>

            {/* ── Pagination Controls ── */}
            {totalPages > 1 && (
              <PaginationWrap>
                <PageBtn
                  onClick={() => goToPage(safePage - 1)}
                  disabled={safePage === 1}
                  title="Previous page"
                >
                  ‹
                </PageBtn>

                {pageButtons.map((p, i) =>
                  p === "…" ? (
                    <PageEllipsis key={`ellipsis-${i}`}>…</PageEllipsis>
                  ) : (
                    <PageBtn
                      key={p}
                      $active={p === safePage}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </PageBtn>
                  )
                )}

                <PageBtn
                  onClick={() => goToPage(safePage + 1)}
                  disabled={safePage === totalPages}
                  title="Next page"
                >
                  ›
                </PageBtn>
              </PaginationWrap>
            )}

            <FooterRight>
              Net volume: <FooterTotal>{fmtMoney(stats.total)}</FooterTotal>
            </FooterRight>
          </TableFooter>
        </TableCard>

        {/* ── Edit Modal — via portal so position:fixed works ── */}
        {editOpen && ReactDOM.createPortal(
          <Overlay onClick={() => { setEditOpen(false); setOtpEntered(""); }}>
            <Modal onClick={e => e.stopPropagation()}>
              <ModalHead>
                <div>
                  <ModalEyebrow>Ledger Entry</ModalEyebrow>
                  <ModalTitle>Edit Record</ModalTitle>
                </div>
                <CloseBtn onClick={() => { setEditOpen(false); setOtpEntered(""); }}>
                  <ClearIcon style={{ fontSize: 18 }} />
                </CloseBtn>
              </ModalHead>

              <ModalBody>
                <FormGrid>
                  <FormField label="Agent">
                    <StyledSelect name="agent" value={agents.find(a => normalize(a) === normalize(form2.agent)) || ""} onChange={handleChange2} options={agents} />
                  </FormField>
                  <FormField label="Supplier">
                    <StyledSelect name="supplier" value={SUPPLIERS.find(s => normalize(s) === normalize(form2.supplier)) || ""} onChange={handleChange2} options={SUPPLIERS} />
                  </FormField>
                  <FormField label="Entry Type">
                    <StyledSelect name="type" value={ENTRY_TYPES.find(t => normalize(t) === normalize(form2.type)) || ""} onChange={handleChange2} options={ENTRY_TYPES} />
                  </FormField>
                  <FormField label="Folder #">
                    <StyledInput name="folderNo" value={form2.folderNo} onChange={handleChange2} />
                  </FormField>
                  <FormField label="PNR">
                    <StyledInput name="pnr" value={form2.pnr} onChange={handleChange2} />
                  </FormField>
                  <FormField label="Cost (£)">
                    <StyledInput name="cost" value={form2.cost} onChange={handleChange2} type="number" />
                  </FormField>
                  <FormField label="Issued By">
                    <StyledInput name="issuedBy" value={form2.issuedBy} onChange={handleChange2} />
                  </FormField>
                  <FormField label="Issuance Date">
                    <StyledInput name="date" value={form2.date} onChange={handleChange2} type="date" />
                  </FormField>
                  <FormField label="Travel Date">
                    <StyledInput name="travelDate" value={form2.travelDate} onChange={handleChange2} type="date" />
                  </FormField>
                  <FormField label="Remarks">
                    <StyledInput name="remarks" value={form2.remarks} onChange={handleChange2} />
                  </FormField>
                </FormGrid>

                <EditCodeSection>
                  <EditCodeLabel>
                    <LockIcon>🔒</LockIcon>
                    Authorization Code
                  </EditCodeLabel>
                  <EditCodeInput
                    type="password"
                    value={otpEntered}
                    onChange={e => setOtpEntered(e.target.value)}
                    placeholder="Enter edit code to confirm changes"
                  />
                </EditCodeSection>
              </ModalBody>

              <ModalFooter>
                <CancelModalBtn onClick={() => { setEditOpen(false); setOtpEntered(""); }}>Cancel</CancelModalBtn>
                <UpdateBtn onClick={handleUpdateEntry}>Save Changes →</UpdateBtn>
              </ModalFooter>
            </Modal>
          </Overlay>,
          document.body
        )}

      </Page>
    </>
  );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  padding: 36px 32px 80px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Geist', sans-serif;
  animation: ${fadeUp} 0.4s ease both;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const HeaderContent = styled.div``;

const HeaderEyebrow = styled.div`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;

const HeaderTitle = styled.h1`
  font-family: 'Instrument Serif', serif;
  font-size: 36px;
  font-weight: 400;
  color: #0f172a;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
  line-height: 1.1;
`;

const TitleAccent = styled.span`
  font-style: italic;
  color: #2563eb;
`;

const HeaderSub = styled.p`
  font-size: 13.5px;
  color: #64748b;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
`;

const BtnBase = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
`;

const BtnIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const ExportBtn = styled(BtnBase)`
  background: white;
  color: #334155;
  border: 1px solid #e2e8f0;
  &:hover { background: #f8fafc; border-color: #cbd5e1; }
`;

const AddBtn = styled(BtnBase)`
  background: #0f172a;
  color: white;
  border: 1px solid transparent;
  &:hover { background: #1e293b; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatCardWrap = styled.div`
  background: ${({ $accent }) => $accent ? "#0f172a" : "white"};
  border: 1px solid ${({ $accent }) => $accent ? "transparent" : "#e2e8f0"};
  border-radius: 14px;
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
  ${({ $accent }) => $accent && `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(37,99,235,0.15) 0%, transparent 60%);
      pointer-events: none;
    }
  `}
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-family: 'Geist Mono', monospace;
  font-size: 22px;
  font-weight: 500;
  color: ${({ $accent }) => $accent ? "white" : "#0f172a"};
  line-height: 1;
  margin-bottom: 4px;
`;

const StatSub = styled.div`font-size: 11.5px; color: #94a3b8;`;

const Panel = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 16px;
  overflow: hidden;
  animation: ${slideDown} 0.25s ease both;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
`;

const PanelDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const PanelBody = styled.div`padding: 20px;`;

const PanelFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 160px;
  flex: 1;
`;

const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #64748b;
`;

const InputEl = styled.input`
  padding: 9px 12px;
  font-size: 13.5px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  appearance: none;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  &::placeholder { color: #cbd5e1; }
  option { background: white; }
`;

const SubmitBtn = styled.button`
  padding: 10px 24px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'Geist', sans-serif;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #1d4ed8; }
`;

const FilterBar = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SearchWrap = styled.div`position: relative;`;

const SearchIconEl = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 17px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px 10px 36px;
  font-size: 13.5px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
  &::placeholder { color: #cbd5e1; }
`;

const FilterControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
  flex: 1;
`;

const FilterLabel = styled.label`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #94a3b8;
`;

const filterBase = `
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  &:focus { border-color: #2563eb; background: white; }
  &::placeholder { color: #cbd5e1; }
  option { background: white; }
`;

const FilterSelect = styled.select`
  ${filterBase}
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-color: #f8fafc;
  padding-right: 30px;
`;

const FilterInput = styled.input`${filterBase}`;

const ClearAllBtn = styled.button`
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  align-self: flex-end;
  transition: all 0.15s;
  &:hover { background: #fee2e2; }
`;

const DateRangeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 260px;
  flex: 1.5;
`;

const DateRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DateRangeInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  box-sizing: border-box;
  transition: all 0.15s;
  min-width: 0;
  &:focus { border-color: #2563eb; background: white; box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
`;

const DateRangeSep = styled.span`
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
`;

const ActiveDateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 12.5px;
  color: #1d4ed8;
`;

const ActiveDateIcon = styled.span`font-size: 13px;`;

const ClearDateBtn = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  color: #1d4ed8;
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.12s;
  &:hover { background: #bfdbfe; }
`;

const TypeTabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const TypeTab = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  ${({ $active, $meta }) => $active
    ? `background: ${$meta ? $meta.bg : "#f1f5f9"}; color: ${$meta ? $meta.color : "#334155"}; border: 1.5px solid ${$meta ? $meta.border : "#e2e8f0"};`
    : `background: white; color: #64748b; border: 1px solid #e2e8f0; &:hover { background: #f8fafc; color: #334155; }`
  }
`;

const TypeCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  background: ${({ $active }) => $active ? "rgba(0,0,0,0.08)" : "#f1f5f9"};
  color: inherit;
`;

const TableCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow-x: auto;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: 'Geist', sans-serif;
  min-width: 1100px;
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: left;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #94a3b8;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  user-select: none;
  cursor: ${({ sortable }) => sortable ? "pointer" : "default"};
  &:hover { color: ${({ sortable }) => sortable ? "#475569" : "#94a3b8"}; }
`;

const SortIndicator = styled.span`
  opacity: ${({ $active }) => $active ? 1 : 0.3};
  color: ${({ $active }) => $active ? "#2563eb" : "inherit"};
  margin-left: 3px;
  font-size: 11px;
`;

const TableRow = styled.tr`
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${({ $idx }) => Math.min($idx * 0.025, 0.4)}s;
  &:hover td { background: #f8fafc; }
  &:not(:last-child) td { border-bottom: 0.5px solid #f1f5f9; }
`;

const Td = styled.td`
  padding: 11px 14px;
  vertical-align: middle;
  color: ${({ $muted }) => $muted ? "#94a3b8" : "#334155"};
  font-family: ${({ $mono }) => $mono ? "'Geist Mono', monospace" : "'Geist', sans-serif"};
  font-size: ${({ $mono }) => $mono ? "12px" : "13px"};
  white-space: nowrap;
`;

const AgentCell = styled.div`display: flex; align-items: center; gap: 8px;`;

const Avatar = styled.div`
  width: 26px; height: 26px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AgentName = styled.span`font-size: 13px; font-weight: 500; color: #0f172a;`;

const FolderTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #f1f5f9;
  border: 0.5px solid #e2e8f0;
  border-radius: 5px;
  font-family: 'Geist Mono', monospace;
  font-size: 11.5px;
  color: #475569;
`;

const CostCell = styled.span`
  font-family: 'Geist Mono', monospace;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $negative }) => $negative ? "#059669" : "#dc2626"};
`;

const SupplierCell = styled.div`display: flex; align-items: center; gap: 6px; font-size: 12.5px;`;
const SupplierDot  = styled.span`width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; opacity: 0.7;`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $border }) => $border};
  white-space: nowrap;
`;

const RemarksText = styled.span`
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #94a3b8;
  font-size: 12.5px;
`;

const EditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px; height: 30px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #fff1f2; color: #dc2626; border-color: #fecaca; }
`;

const EmptyState = styled.div`text-align: center; padding: 64px 24px;`;

const EmptyIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
  opacity: 0.2;
  display: inline-block;
  animation: ${rotate} 1.5s linear infinite;
`;

const EmptyTitle = styled.div`font-size: 15px; font-weight: 600; color: #64748b; margin-bottom: 4px;`;
const EmptyDesc  = styled.div`font-size: 13px; color: #94a3b8;`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 10px;
`;

const FooterLeft   = styled.div`font-size: 12px; color: #94a3b8; font-family: 'Geist Mono', monospace;`;
const FooterStrong = styled.strong`color: #64748b; font-weight: 500;`;
const FooterRight  = styled.div`font-size: 12px; color: #94a3b8; font-family: 'Geist Mono', monospace;`;
const FooterTotal  = styled.span`font-weight: 600; color: #0f172a; font-size: 14px; margin-left: 4px;`;

// ── Pagination ──

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 6px;
  font-size: 12.5px;
  font-weight: ${({ $active }) => $active ? "600" : "400"};
  font-family: 'Geist Mono', monospace;
  border-radius: 7px;
  cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
  transition: all 0.15s;
  border: 1px solid ${({ $active }) => $active ? "#2563eb" : "#e2e8f0"};
  background: ${({ $active }) => $active ? "#2563eb" : "white"};
  color: ${({ $active, disabled }) => $active ? "white" : disabled ? "#cbd5e1" : "#475569"};
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  &:hover:not(:disabled) {
    background: ${({ $active }) => $active ? "#1d4ed8" : "#f1f5f9"};
    border-color: ${({ $active }) => $active ? "#1d4ed8" : "#cbd5e1"};
  }
`;

const PageEllipsis = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 30px;
  font-size: 12px;
  color: #94a3b8;
  font-family: 'Geist Mono', monospace;
  user-select: none;
`;

/* ── Modal ── */

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100vw; height: 100vh;
  background: rgba(15,23,42,0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  animation: ${fadeIn} 0.18s ease both;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: white;
  border-radius: 18px;
  width: min(860px, 94vw);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 64px rgba(0,0,0,0.2);
  animation: ${scaleIn} 0.25s ease both;
  display: flex;
  flex-direction: column;
`;

const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalEyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const ModalTitle = styled.div`
  font-family: 'Instrument Serif', serif;
  font-size: 22px;
  font-weight: 400;
  color: #0f172a;
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { background: #f1f5f9; }
`;

const ModalBody = styled.div`padding: 24px 28px; flex: 1;`;

const EditCodeSection = styled.div`
  margin-top: 4px;
  padding: 16px;
  background: #fefce8;
  border: 1px solid #fef08a;
  border-radius: 10px;
`;

const EditCodeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: #854d0e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const LockIcon = styled.span`font-size: 13px;`;

const EditCodeInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 14px;
  font-family: 'Geist', sans-serif;
  border: 1.5px solid #fde68a;
  border-radius: 8px;
  background: white;
  color: #0f172a;
  outline: none;
  transition: all 0.15s;
  &:focus { border-color: #f59e0b; box-shadow: 0 0 0 3px rgba(245,158,11,0.12); }
  &::placeholder { color: #d1d5db; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 28px 24px;
  border-top: 1px solid #f1f5f9;
`;

const CancelModalBtn = styled.button`
  padding: 10px 20px;
  font-size: 13.5px;
  font-weight: 500;
  font-family: 'Geist', sans-serif;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f8fafc; }
`;

const UpdateBtn = styled.button`
  padding: 10px 24px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: 'Geist', sans-serif;
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #1e293b; }
`;

export default FlightSupplierLedger;